/* @jsxImportSource @emotion/react */

import type { HTMLAttributes, SelectHTMLAttributes } from 'react'
import React from 'react'

import type { PopoverSelectOption } from '../PopoverSelect/PopoverSelect'
import { PopoverSelect } from '../PopoverSelect/PopoverSelect'
import { mergeRefs } from '../utils/mergeRefs'
import type { InputSize } from './constants'
import { SelectButton } from './SelectButton'

export type SelectProps<T extends PopoverSelectOption> = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'size' | 'className' | 'onChange' | 'value' | 'placeholder' | 'ref'
> & {
  hasError?: boolean
  wrapperClassName?: string
  wrapperStyle?: HTMLAttributes<HTMLDivElement>['style']
  inputClassName?: string
  size?: InputSize
  placeholder: string
  options: T[]
  value?: T['key'] | T['key'][]
  onChange?: (value: T['key']) => void
  ref?: React.Ref<HTMLElement>
}

export function Select<T extends PopoverSelectOption>({
  inputClassName,
  hasError,
  wrapperClassName,
  wrapperStyle,
  style,
  value,
  size = 'medium',
  placeholder,
  options,
  onChange,
  ref,
  ...props
}: SelectProps<T>) {
  const [isOpen, setIsOpen] = React.useState(false)

  return (
    <PopoverSelect<T>
      onSelectOption={(option) => onChange?.(option.key)}
      options={options}
      {...(Array.isArray(value)
        ? { selectedMultiValueKeys: value }
        : { selectedValueKey: value })}
      onIsOpenChange={setIsOpen}
      disabled={props.disabled}
      style={{
        maxHeight: '30vh',
      }}
      target={({ ref: targetRef, toggle, value }) => (
        <SelectButton
          ref={mergeRefs(targetRef, ref)}
          hasError={hasError}
          isOpen={isOpen}
          size={size}
          style={style}
          placeholder={placeholder}
          disabled={props.disabled}
          inputClassName={inputClassName}
          onClick={toggle}
        >
          {value?.title}
        </SelectButton>
      )}
    />
  )
}
